<template>
  <div class="pricing-table">
    <Slider
      v-model:selected-option="attendance"
      :options="[...attendanceTypes]"
      :abbreviations="abbreviations" />
    <div class="discount-buttons div-needed-for-equal-borders">
      <p>Available discounts (select all that apply):</p>
      <ButtonRow
        v-model:selected-options="selectedDiscountsModel"
        :options="discountTypes"
        :abbreviations="abbreviations" />
    </div>
    <FeeTable
      v-for="(event, eventName) in events"
      :key="eventName"
      :title="eventName"
      :data="addDiscountedFees(event, attendance)"
      :abbreviations="abbreviations"
      :currency="currency" />
  </div>
</template>

<script setup>
const log = (msg) => process.env.VUE_APP_DEV && console.log(`calculator: ${msg}`); // eslint-disable-line
const props = defineProps(["options"]);
import {ref, computed, watch} from "vue";
import {useStorage} from "@vueuse/core";
import {useRouter} from "vue-router";
import history from "@/assets/history.json";
import Slider from "@/components/App/Slider";
import ButtonRow from "@/components/App/ButtonRow";
import FeeTable from "@/components/Registration/FeeTable";
const router = useRouter();

const events = computed(() => props.options?.events ?? []);
const discounts = computed(() => props.options?.discounts ?? []);
const abbreviations = computed(() => props.options?.abbreviations ?? []);
// optional: a page whose fees aren't in history.json's currency (e.g. 2026's online events, $)
const currency = computed(() => props.options?.currency);
const isEarlyBirdPast = computed(() => props.options?.isEarlyBirdPast ?? []);
const isVirtualDefault = computed(() => props.options?.isVirtualDefault ?? false);

const attendanceTypes = new Set();
{
  for (const [, eventData] of Object.entries(events.value)) {
    if (typeof eventData !== "object" || eventData === null) continue;
    for (const [key, value] of Object.entries(eventData)) {
      if (typeof value !== "object" || value === null) continue;
      attendanceTypes.add(key);
    }
  }
  if (attendanceTypes.sizes === 0) throw new Error(`No attendanceTypes found`);
}
const firstAttendanceType = attendanceTypes.values().next().value; // painful!
const attendance = ref(isVirtualDefault.value ? "Virtual" : firstAttendanceType);
const inPersonNoticeShown = useStorage("in-person-notice-shown", false);
watch(attendance, (newVal) => {
  if (newVal !== "In-person" || !isVirtualDefault.value || inPersonNoticeShown.value) return;
  inPersonNoticeShown.value = true;
  router.push({hash: "#in-person-closed"});
});

function sortDiscountTypes(keys) {
  return keys.sort((a, b) => {
    const aLower = a.toLowerCase();
    const bLower = b.toLowerCase();
    if (aLower.includes("regional") && !bLower.includes("regional")) return -1;
    if (!aLower.includes("regional") && bLower.includes("regional")) return 1;
    if (aLower.includes("early") && !bLower.includes("early")) return -1;
    if (!aLower.includes("early") && bLower.includes("early")) return 1;
    return 0; // Keep other items in any order
  });
}

const discountTypes = computed(() => {
  const keys = sortDiscountTypes(Object.keys(discounts.value));
  if (!isEarlyBirdPast.value) return keys;
  return keys.filter((key) => !key.toLowerCase().match(/early[\s-]*bird/));
});

function isEarlyBirdDiscount(label) {
  if (!label) return false;
  return /early[\s-]*bird/i.test(label);
}

function getInitialSelectedDiscounts() {
  const keys = discountTypes.value;
  if (!Array.isArray(keys) || keys.length === 0) return [];
  if (isEarlyBirdPast.value) return keys.slice(0, 1);
  const earlyBirdKey = keys.find((key) => isEarlyBirdDiscount(key));
  if (earlyBirdKey) return [earlyBirdKey];
  return keys.slice(0, 2);
}

const selectedDiscounts = ref(getInitialSelectedDiscounts());

function isMemberDiscount(label) {
  if (!label) return false;
  return label.trim().toLowerCase().endsWith("member");
}

function getAddedItem(next, prev) {
  for (const item of next) {
    if (!prev.includes(item)) return item;
  }
  return null;
}

function getResolvedDiscountSelection(next, prev) {
  if (!Array.isArray(next)) return prev;
  if (!Array.isArray(prev)) return next;
  const added = getAddedItem(next, prev);
  if (!added) return next;
  if (!isMemberDiscount(added)) return next;
  return next.filter((item) => item === added || !isMemberDiscount(item));
}

const selectedDiscountsModel = computed({
  get() {
    return selectedDiscounts.value;
  },
  set(next) {
    const prev = selectedDiscounts.value;
    const resolved = getResolvedDiscountSelection(next, prev);
    log(`model:set prev=${JSON.stringify(prev)}`);
    log(`model:set next=${JSON.stringify(next)}`);
    log(`model:set resolved=${JSON.stringify(resolved)}`);
    selectedDiscounts.value = resolved;
  }
});

watch(
  discountTypes,
  (next) => {
    if (!Array.isArray(next) || next.length === 0) return;
    const filtered = selectedDiscounts.value.filter((item) => next.includes(item));
    if (filtered.length === 0) {
      selectedDiscounts.value = getInitialSelectedDiscounts();
      return;
    }
    selectedDiscounts.value = getResolvedDiscountSelection(filtered, selectedDiscounts.value);
  },
  {immediate: true}
);

const calculateDiscountedFee = (fullFee) => {
  log(`pricing:selectedDiscounts=${JSON.stringify(selectedDiscounts.value)}`);
  const additive = history.discountCalculation === "additive" ? true : false;
  let totalDiscountRate = additive ? 0 : 1;
  for (const [key, rate] of Object.entries(discounts.value)) {
    if (!selectedDiscounts.value.includes(key)) continue;
    if (additive) {
      totalDiscountRate += rate; // Sum rates
    } else {
      totalDiscountRate *= 1 - rate; // multiplicative by default
    }
  }
  const effectiveRate = additive ? totalDiscountRate : 1 - totalDiscountRate;
  return Math.round(fullFee * (1 - effectiveRate));
};

function addDiscountedFees(event, attendance) {
  if (!event?.[attendance]) {
    // Build "in-person only" placeholder, preserving categories
    const inPerson = event?.["In-person"];
    const categories = Object.keys(inPerson?.["Full Fee"] ?? {});
    const placeholder = {};
    for (const cat of categories) placeholder[cat] = "In-person only";
    return placeholder; // <-- flat key->string object
  }
  if (!event[attendance]) {
    console.log(`Registration: addDiscountedFees = ${JSON.stringify(event)}`);
    return event;
  }
  const fees = event[attendance];
  console.log(`Registration: fees = (${JSON.stringify(fees)})`);
  if (typeof fees !== "object") {
    console.log("Registration: 'fees' not an object.");
    return fees;
  }
  const keys = Object.keys(fees);
  if (keys.length !== 1) {
    console.log("Registration: 'fees' has <> 1 property.");
    return fees;
  }
  const key = keys[0]; // The single property name
  if (typeof fees[key] !== "object") {
    ("Registration: 'fees[key]' not object.");
    return fees;
  }
  const discountedFees = {};
  for (const [key, fullFee] of Object.entries(fees[key])) {
    if (typeof fullFee !== "number") {
      console.log(`Registration: '${fullFee}' not a number`);
      return fees;
    }
    discountedFees[key] = calculateDiscountedFee(fullFee);
  }
  const addDiscountedFees = {...fees, "Discounted Fee": discountedFees};
  console.log(`Registration: addDiscountedFees = ${JSON.stringify(addDiscountedFees)}`);
  return addDiscountedFees;
}
</script>

<style scoped>
:is(*, *::before, *::after) {
  box-sizing: border-box;
}
.pricing-table {
  display: flex;
  flex-direction: column;
  /* gap: 16px; Space between items */
  background-color: #dfdfdf;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 0 8px;
}
.pricing-table > * {
  border-bottom: 2px solid #f4f4f4; /* Add border to separate items */
  margin-bottom: 16px; /* Add spacing after each element */
  padding-bottom: 16px; /* Ensure padding is added before the border */
}
.pricing-table > *:first-child {
  margin-top: 16px; /* Matches the bottom spacing of other elements */
}
.pricing-table > *:last-child {
  border-bottom: none; /* Remove border for the last child */
  margin-bottom: 0; /* Prevent extra spacing after the last child */
  padding-bottom: 0; /* Remove padding as well */
}
p {
  text-align: center;
  margin-top: 0px;
  font-size: 1.1rem;
  font-weight: 300;
  color: rgb(148, 148, 148);
}
:is(.unlimited, .wide, .medium) p {
  font-size: 1.3rem;
}
</style>
